import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMediaQuery } from "react-responsive";
import AgreementDetails from "./AgreementDetails";
import PaymentMethods from "./PaymentMethods";
import SignAgreement from "./SignAgreement";
import PaymentAgreementProcessSucces from "./PaymentAgreementProcessSucces";
import PaymentAgreementFailed from "./PaymentAgreementFailed";
import {
  agreementEmail,
  createPaypalPaymentToken,
  createPaypalToken,
  duplicatePaymentGet,
  paymentDownload,
  paymentReceiptAgreementDownloadPdf,
  resetCheckDuplicatePayment,
  resetDuplicateOrder,
  setIsPaymentProcessing,
  signAgreement,
  storeAgreementSignature,
  storeAgreementConsent,
  storePayPalOrder,
} from "store/slices/paymentsSlice";
import {
  ActionPaymentMethodProps,
  CardData,
  ScheduledItem,
  SignAgreementValues,
  PaymentProcessResponse,
  PlaidBankPaymentValues,
  GooglePayData,
  PayPalOrderData,
  DuplicatePaymentPayload,
  VerifyStripeCardPaymentParams,
  VerifyStaxCardPaymentParams,
} from "types/agreement";
import { GooglePaySelection } from "types/paymentLink";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { toBlob } from "html-to-image";
import {
  leadData,
  useLeadData,
  usePortalSettingsData,
} from "store/slices/dashboardSlice";
import {
  onSetFinalDisclosureLoading,
  onSetShowDisclosure,
  useIsLogin,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import { addNotification, useUserData } from "store/slices/authSlice";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  cardToShortname,
  checkBankManualFormOrPlaid,
  decode,
  downloadPdfFromApi,
  getBrowserTimezoneName,
  getBrowserTimezoneOffset,
} from "utils/HelperFunctions";
import Stepper from "components/ui-component/Stepper";
import moment from "moment";
import DisclosureFinal from "components/makePayment/DisclosureFinal";
import { FormRef, PaymentGateway, SettingsValue } from "types/payments";
import CustomModal from "components/ui-component/CustomModal";
import { RootState } from "store/store";
import { BASE_URL } from "store/api/constApi";
import { staxProfile } from "store/slices/paymentMethodSlice";
import { TestIdScope, useTestAttrsFactory } from "utils/testid";
import StepCheckIcon from "icons/action/StepCheckIcon";
import { buildAgreementReceiptEmailPayload } from "utils/agreementReceiptEmail";
import {
  isSavedForAgreement,
  signatureForAgreement,
} from "store/services/agreementSignatureCache";

// Desktop rail geometry, matching Figma (41px circles joined by 66px of line,
// so steps sit 107px apart). Steps 2 and 3 open with a 1px divider plus 32px of
// padding (lg:border-t lg:pt-8) before their 40px title row, and the grid's
// 33px row gap follows it, so a collapsed title sits centered between dividers.
// Their circles drop by the divider offset to stay level with the title.
const STEP_RAIL_ROW_GAP = 33; // matches the grid's gap-y-[33px]
const STEP_RAIL_CIRCLE_SIZE = 41;
const STEP_RAIL_DIVIDER_OFFSET = 33;

const DesktopStepRailItem = ({
  step,
  currentStep,
  completedSteps,
  isLastStep = false,
  circleOffset = 0,
  nextCircleOffset = 0,
  onClick,
}: {
  step: number;
  currentStep: number;
  completedSteps: number[];
  isLastStep?: boolean;
  circleOffset?: number;
  nextCircleOffset?: number;
  onClick: (step: number) => void;
}) => {
  const isActive = currentStep === step;
  const isCompleted = completedSteps.includes(step);
  const canNavigate = isCompleted && step < currentStep;
  const isCompletedConnector = currentStep > step;

  return (
    <div
      className="relative h-full w-[41px]"
      data-testid={`action-payment-desktop-step-${step}`}
    >
      {!isLastStep && (
        <div
          className="absolute left-1/2 z-10 w-px -translate-x-1/2"
          style={{
            top: circleOffset + STEP_RAIL_CIRCLE_SIZE,
            bottom: -(STEP_RAIL_ROW_GAP + nextCircleOffset),
          }}
          data-testid={`action-payment-step-connector-${step}`}
          aria-hidden="true"
        >
          {isActive ? (
            // Figma: from the current step the line runs solid blue for the
            // first half, dashed blue for the second, then turns grey from the
            // next step's divider down to its circle.
            <div className="flex h-full w-full flex-col">
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex-1 bg-btnPrimaryActive" />
                <div
                  className="flex-1"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(to bottom, transparent 0 1.95px, var(--color-btn-primary-active) 1.95px 5.85px, transparent 5.85px 7.8px)",
                  }}
                />
              </div>
              <div
                className="shrink-0 bg-strokePrimary"
                style={{ height: nextCircleOffset }}
              />
            </div>
          ) : (
            <div
              className={`h-full w-full ${
                isCompletedConnector ? "bg-btnOutlineStroke" : "bg-strokePrimary"
              }`}
            />
          )}
        </div>
      )}
      <button
        type="button"
        style={{ marginTop: circleOffset }}
        className={`relative z-20 flex h-[41px] w-[41px] items-center justify-center rounded-full border-[1px] ${
          isActive
            ? "border-btnPrimaryActive bg-btnPrimaryActive text-white"
            : isCompleted
              ? "border-btnOutlineStroke bg-white text-btnPrimaryActive"
              : "border-strokePrimary bg-white text-txtSecondary"
        } ${canNavigate ? "cursor-pointer" : "cursor-default"}`}
        aria-current={isActive ? "step" : undefined}
        aria-label={`Step ${step}${
          isActive ? ", current" : isCompleted ? ", completed" : ""
        }`}
        disabled={!canNavigate}
        onClick={() => onClick(step)}
      >
        {isCompleted ? <StepCheckIcon aria-hidden="true" /> : <span>{step}</span>}
      </button>
    </div>
  );
};

const ActionPaymentMethodContent = (props: ActionPaymentMethodProps) => {
  const {
    scheduled,
    orderData,
    paymentAgreementProcess,
    handleGooglePay,
    isGooglePayLoading,
    isSigned,
    setInitialDisclosureAccept = () => {},
    generalMarkers,
    action,
    handleStatusOnAccept = () => {},
    scrollDocumentToTop = () => {},
  } = props;
  const testAttrs = useTestAttrsFactory();
  const isDesktopViewport = useMediaQuery({ query: "(min-width: 1024px)" });
  const dispatch = useAppDispatch();
  const portalSettings = usePortalSettingsData();
  const showFinalDisclosure = useShowFinalDisclosure();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([0]);
  const [confirmAgreement, setConfirmAgreement] = useState(isSigned);
  const [screen, setScreen] = useState<boolean>(isSigned);
  const [isFee, setIsFee] = useState<boolean>(false);
  const [cardData, setCardData] = useState<CardData | null>(null);
  const [failedReason, setFailedReason] = useState<string | null>(null);
  const [amount, setAmount] = useState<string | undefined>();
  const [invoiceId, setInvoiceId] = useState<string | null>();
  const [emails, setEmails] = useState<string>("");
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [storePayload, setStorePayload] = useState<CardData | null>(null);
  const cashAppRef = useRef<FormRef>(null);
  const bankRef = useRef<FormRef>(null);
  const cardRef = useRef<FormRef>(null);
  const amazonRef = useRef<FormRef>(null);
  const applePayAgreementInFlightRef = useRef<Set<string>>(new Set());
  const sentReceiptInvoiceIdsRef = useRef<Set<string>>(new Set());
  const [tokenData, setTokenData] = useState<GooglePaySelection | null>(null);
  const [scheduleId, setScheduleId] = useState<number | null>(null);
  const [isManualForm, setIsManualForm] = useState<boolean>(true);
  const {
    payPalOrder,
    agreementSign,
    paymentPermission,
    checkedDuplicatePayment,
    isDuplicateOrders,
    duplicateOrdersCount,
  } = useAppSelector((state: RootState) => state.payment);
  const [isScreenLoading, setIsScreenLoading] = useState<boolean>(true);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [selectedMethod, setSelectedMethod] = useState<
    "Card" | "Bank" | "PayPal" | "Cash App Pay" | string
  >("Card");

  const stepperAnchorRef = useRef<HTMLDivElement>(null);
  const disclosureRef = useRef<HTMLDivElement>(null);
  const paymentFlowTopRef = useRef<HTMLDivElement>(null);
  const pendingButtonScrollRef = useRef<boolean>(false);
  const pendingButtonScrollTargetRef = useRef<"top" | "stepper" | "disclosure">(
    "top"
  );

  const scrollToRefTop = useCallback(
    (
      targetRef: React.RefObject<HTMLDivElement>,
      shouldResetDocument: boolean = true
    ) => {
      const scrollToTop = () => {
        let parent = targetRef.current?.parentElement;

        while (parent) {
          if (parent.scrollHeight > parent.clientHeight) {
            parent.scrollTo({ top: 0, behavior: "auto" });
          }
          parent = parent.parentElement;
        }

        targetRef.current?.scrollIntoView({
          behavior: "auto",
          block: "start",
        });

        if (shouldResetDocument) {
          document.scrollingElement?.scrollTo({ top: 0, behavior: "auto" });
          window.scrollTo({ top: 0, behavior: "auto" });
          scrollDocumentToTop();
        }
      };

      scrollToTop();
      requestAnimationFrame(scrollToTop);
      window.setTimeout(scrollToTop, 0);
    },
    [scrollDocumentToTop]
  );

  const scrollPaymentFlowToTop = useCallback(() => {
    scrollToRefTop(paymentFlowTopRef);
  }, [scrollToRefTop]);

  const scrollStepperToTop = useCallback(() => {
    scrollToRefTop(stepperAnchorRef, false);
  }, [scrollToRefTop]);

  const scrollDisclosureToTop = useCallback(() => {
    scrollToRefTop(disclosureRef, false);
  }, [scrollToRefTop]);

  const requestButtonScroll = (
    target: "top" | "stepper" | "disclosure" = "top"
  ): void => {
    pendingButtonScrollRef.current = true;
    pendingButtonScrollTargetRef.current = target;
  };

  const runPendingButtonScroll = useCallback((): void => {
    if (!pendingButtonScrollRef.current) return;

    pendingButtonScrollRef.current = false;
    const scroll = {
      disclosure: scrollDisclosureToTop,
      stepper: scrollStepperToTop,
      top: scrollPaymentFlowToTop,
    }[pendingButtonScrollTargetRef.current];

    scroll();
    window.setTimeout(scroll, 100);
  }, [scrollDisclosureToTop, scrollPaymentFlowToTop, scrollStepperToTop]);

  const [signUrl, setSignUrl] = useState<string | null>(null);

  const accountLeadData = useLeadData();
  const userData = useUserData();
  const isLogin = useIsLogin();
  const { id: actionId } = useParams();
  // The persisted copies are only trusted on the agreement they were drawn for.
  const agreementSignBase64 = signatureForAgreement(agreementSign, actionId);
  const savedSignUrl = signUrl || agreementSignBase64;
  const savedConsentAt = isSavedForAgreement(agreementSign, actionId)
    ? agreementSign?.esign_consent_at
    : undefined;
  const navigate = useNavigate();

  const onGetLeadData = async () => {
    let lead_payload: any = {
      accounts_id: userData?.data?.accountData?.id,
      cluster_id: userData?.data?.accountData?.clusterid,
      accountid: userData?.data?.accountData?.accountid,
    };
    try {
      await dispatch(leadData(lead_payload)).unwrap();
    } catch (error: any) {
      console.log("onGetLeadData error", error);
    }
  };

  useEffect(() => {
    if (!actionId) return;
    const requestOptions = {
      method: "GET",
      redirect: "follow",
    };

    fetch(
      `${BASE_URL}/portal/get-payment-status?slug=${actionId}&type=agreement`,
      requestOptions as any
    )
      .then((response) =>
        handleStreamResponse(response, (res: any) => {
          const match = res.match(
            /"success":\s*(true|false).*?"order_id":\s*(\d+).*?"amount":\s*"([^"]*)".*?"total_amount":\s*"([^"]*)".*?"schedule_id":\s*(\d+).*?"email":\s*"([^"]*)"/
          );

          console.log("handleStreamResponse match =====", match);

          if (match) {
            if (match[1] === "true") {
              let amount = match[4] ? match[4] : match[3];
              setConfirmAgreement(true);
              setScreen(true);
              handleStatusOnAccept();
              setAmount(amount);
              setEmails(match[6]);
              setInvoiceId(match[2]);
              setScheduleId(parseInt(match[5], 10));
            }
          }
        })
      )
      .then((result) => console.log(result))
      .catch((error) => console.error(error));
  }, [actionId]);

  const handleStreamResponse = async (
    response: Response,
    onSuccessCallback: (chunk: string) => void
  ): Promise<void> => {
    const reader = response.body?.getReader();
    if (!reader) return;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          console.log("Stream finished.");
          break;
        }
        const chunk = new TextDecoder().decode(value);

        onSuccessCallback(chunk);
      }
    } catch (error) {
      console.error("Error reading the stream", error);
      // isConnectionClosed(true); // Assume the connection is closed on error
    }
  };

  useEffect(() => {
    if (
      isLogin &&
      accountLeadData &&
      Object.keys(accountLeadData).length === 0
    ) {
      onGetLeadData();
    }
  }, [isLogin]);

  const handleNext = (): void => {
    requestButtonScroll("stepper");
    const tmp = Array.from(completedSteps);
    tmp.push(currentStep);
    setCurrentStep(currentStep + 1);
    setCompletedSteps(tmp);
  };
  // isAgreement
  const onDownloadPdf = async (): Promise<void> => {
    let slug = decode(actionId || "")?.split("_");
    let params = {
      plan_id: slug[0],
      lead_id: orderData?.lead_id,
      url: window.location.href,
      cluster_id: slug[1],
    };

    await downloadPdfFromApi(
      () => dispatch(paymentReceiptAgreementDownloadPdf(params)).unwrap(),
      `Payment_Agreement - ${slug[0]}.pdf`
    );
  };

  const onSendEmail = async (values: {
    plan_id: string;
    emails: string[] | string;
  }): Promise<void> => {
    try {
      const validEmails = Array.isArray(values.emails)
        ? values.emails
            .map((email: any) =>
              typeof email === "string" ? email.trim() : ""
            )
            .filter(Boolean)
        : typeof values.emails === "string" && values.emails.trim()
          ? [values.emails.trim()]
          : [];

      if (!values.plan_id || !orderData?.lead_id || validEmails.length === 0) {
        console.warn("onSendEmail skipped: missing required values", {
          plan_id: values.plan_id,
          lead_id: orderData?.lead_id,
          emails: values.emails,
        });
        return;
      }

      let payload = {
        plan_id: values.plan_id,
        lead_id: orderData?.lead_id,
        email: validEmails,
      };
      await dispatch(agreementEmail(payload)).unwrap();
    } catch (error) {
      console.log("onSendEmail error =====", error);
    }
  };

  const handleUpdatePaymentMethod = (): void => {
    requestButtonScroll("stepper");
    dispatch(setIsPaymentProcessing(false));
    dispatch(resetDuplicateOrder());
    dispatch(resetCheckDuplicatePayment());
    dispatch(onSetFinalDisclosureLoading(false));
    dispatch(onSetShowDisclosure(false));
    setCurrentStep(3);
    setCompletedSteps([0, 1, 2]);
    setConfirmAgreement(false);
    setScreen(false);
    setIsScreenLoading(false);
    setIsConfirmLoading(false);
    setCardData(null);
    setStorePayload(null);
    setTokenData(null);
    setFailedReason(null);
  };

  const handleBack = () => {
    const tmp = Array.from(completedSteps);
    tmp.pop(); // Remove the last completed step when going back

    setCurrentStep(currentStep - 1);
    setCompletedSteps(tmp);
  };

  const [tempdata, setTempData] = useState<ScheduledItem[]>([]);

  const sendEmail = async ({
    invoice_id,
    emails: receiptEmails,
  }: {
    invoice_id?: string | number | null;
    emails?: string | string[] | null;
  }): Promise<void> => {
    const billingDate = scheduled?.[0]?.billing_date;
    const isRealTimePayment = Boolean(
      billingDate &&
      moment(billingDate).isValid() &&
      moment(billingDate).isSame(moment(), "day")
    );
    const receiptPayload = buildAgreementReceiptEmailPayload({
      isPaymentSuccessful: true,
      isRealTimePayment,
      invoiceId: invoice_id,
      accountId: userData?.data?.accountData?.accountid,
      emails: receiptEmails || userData?.data?.accountData?.email,
    });

    if (!receiptPayload) {
      return;
    }

    const receiptKey = String(receiptPayload.invoice_id);
    if (sentReceiptInvoiceIdsRef.current.has(receiptKey)) {
      return;
    }

    sentReceiptInvoiceIdsRef.current.add(receiptKey);
    try {
      await dispatch(paymentDownload(receiptPayload)).unwrap();
    } catch (error) {
      console.error("Automatic agreement payment receipt email failed", error);
    }
  };

  useEffect(() => {
    console.log("scheduled", scheduled);
    if (scheduled) {
      let arr = [...scheduled];
      setIsFee(arr.some((obj) => obj.cf_amount > 0));
      setTempData(scheduled);
    }
  }, [scheduled]);

  const blobToBase64 = (blob: Blob): Promise<string | ArrayBuffer | null> => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    return new Promise((resolve) => {
      reader.onloadend = () => {
        resolve(reader.result);
      };
    });
  };

  const storeSign = async (finalSign: {
    signTitle?: string;
    fileURL?: string;
    consentAt?: string;
  }): Promise<void> => {
    let img: string | ArrayBuffer | null = "",
      blobImg;

    if (finalSign?.signTitle !== "") {
      let sign = document.querySelector(".signInputClass");
      if (sign) {
        blobImg = await toBlob(sign as HTMLElement);
        if (blobImg) {
          img = await blobToBase64(blobImg);
        }
      } else {
        return;
      }
    } else if (finalSign.fileURL && finalSign.fileURL !== "") {
      img = finalSign.fileURL;
    } else {
      return;
    }
    setSignUrl(img as string);
    // E-SIGN consent time (consent-step clusters only); signAgreement adds it
    // to the sign-agreement payload.
    await dispatch(
      storeAgreementSignature({
        sign_base64: img as string,
        slug: actionId,
        ...(finalSign.consentAt
          ? { esign_consent_at: finalSign.consentAt }
          : {}),
      })
    );
    handleNext();
  };

  // Saved straight away so the desktop and mobile copies of the signature
  // step share it and it outlives the wizard closing.
  const storeConsent = (consentAt: string): void => {
    if (!actionId) return;
    dispatch(
      storeAgreementConsent({ slug: actionId, esign_consent_at: consentAt })
    );
  };

  const handleConfirm = async (
    payload: CardData,
    isCCTokenAvailable: boolean = false
  ): Promise<void> => {
    requestButtonScroll();
    let slug = decode(actionId || "")?.split("_");
    dispatch(setIsPaymentProcessing(true));
    setIsConfirmLoading(true);
    try {
      let resp: PaymentProcessResponse | undefined;
      let value: SignAgreementValues = {
        cardData: { ...payload },
        sign_base64: savedSignUrl,
        isCCTokenAvailable,
      };
      resp = await paymentAgreementProcess(value);
      if (resp?.body?.success) {
        setAmount(resp?.body?.data?.total_amount);
        setEmails(resp?.body?.data?.email);
        setInvoiceId(resp?.body?.data?.invoice_id);
        setScheduleId(resp?.body?.data?.schedule_id);
        setScreen(true);
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_agreement",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "accepted",
            activity_type: "payment_activity",
            clusterid: slug[1],
            communication_status: "",
            accounts_id: orderData?.accounts_id,
            payment_amount: orderData?.grandTotal,
            plan_id: slug[0],
          })
        );
        handleStatusOnAccept();
      } else {
        setFailedReason(resp?.body?.data?.msg || "Payment failed");
        setScreen(false);
      }
      setIsConfirmLoading(false);
      setConfirmAgreement(true);
    } catch (error) {
      setIsConfirmLoading(false);
      setScreen(false);
      console.log("handleConfirm error =====", error);
    } finally {
      dispatch(setIsPaymentProcessing(false));
    }
  };

  const handleApprove = async (): Promise<void> => {
    let slug = decode(actionId || "")?.split("_");
    dispatch(setIsPaymentProcessing(true));
    try {
      let res = await handleGooglePay(
        "PP",
        undefined,
        "",
        searchParams.get("token") || ""
      );
      if (res?.body?.success) {
        setAmount(res?.body?.data?.total_amount);
        setEmails(res?.body?.data?.email);
        setInvoiceId(res?.body?.data?.invoice_id);
        setScheduleId(res?.body?.data?.schedule_id);
        setScreen(true);
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_agreement",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "accepted",
            activity_type: "payment_activity",
            clusterid: slug[1],
            communication_status: "",
            accounts_id: orderData?.accounts_id,
            payment_amount: orderData?.grandTotal,
            plan_id: slug[0],
          })
        );
        handleStatusOnAccept();
      } else {
        setFailedReason(res.body.data.msg || "Payment failed");
        setScreen(false);
      }
      setConfirmAgreement(true);
      setIsScreenLoading(false);
    } catch (error) {
      console.log("handleApprove error res =====", error);
    } finally {
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      await dispatch(onSetFinalDisclosureLoading(false));
      await dispatch(onSetShowDisclosure(false));
    }
  };

  const handleOnClick = (step: number) => {
    if (step < currentStep) {
      setCurrentStep(step);
      setCompletedSteps(Array.from({ length: step }, (_, i) => i));
    } else {
      return;
    }
  };

  const handleBankPayment = async (
    values: PlaidBankPaymentValues
  ): Promise<void> => {
    let slug = decode(actionId || "")?.split("_");
    dispatch(setIsPaymentProcessing(true));
    setIsScreenLoading(true);
    let payload = {
      plan_id: slug?.[0],
      lead_id: orderData?.lead_id,
      x_method: values?.method,
      transaction_id: values?.transaction_id,
      x_email: userData?.data?.accountData?.email || "",
      creditcard: "",
      choldername: "",
      aholdername: "",
      desc: "Online Payment",
      description: "Online Payment",
      payment_note: "Payment Agreement",
      cvv: "***",
      expiry: "",
      card_type: "",
      card_last4: "",
      routing_number: "",
      account_number: "",
      bank_name: "",
      bank_address: "",
      search_address: "",
      sign_base64: agreementSignBase64,
      is3ds: values?.method === "CC" ? true : false,
      clusterid: slug?.[1],
      timezone: getBrowserTimezoneOffset(),
      customer_timezone: getBrowserTimezoneName(),
      slug: actionId || "",
      custom_schedule: 'Y'
    };
    try {
      const res: any = await dispatch(signAgreement(payload)).unwrap();
      if (res?.body?.success) {
        setAmount(res?.body?.data?.total_amount);
        setEmails(res?.body?.data?.email);
        setInvoiceId(res?.body?.data?.invoice_id);
        setScheduleId(res?.body?.data?.schedule_id);
        setScreen(true);
        await sendEmail({
          invoice_id: res?.body?.data?.invoice_id,
          emails: res?.body?.data?.email,
        });
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_agreement",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "accepted",
            activity_type: "payment_activity",
            clusterid: slug[1],
            communication_status: "",
            accounts_id: orderData?.accounts_id,
            payment_amount: orderData?.grandTotal,
            plan_id: slug[0],
          })
        );
        handleStatusOnAccept();
      } else {
        setFailedReason(res.body.data.msg);
        setScreen(false);
      }
      setConfirmAgreement(true);
      setIsScreenLoading(false);
      await dispatch(
        addNotification({
          initiated_by: "contact",
          event_type: "payment_agreement",
          userid: userData?.data?.accountData?.leadData?.user_id,
          email: "",
          event_status: "accepted",
          activity_type: "payment_activity",
          clusterid: slug[1],
          communication_status: "",
          accounts_id: orderData?.accounts_id,
          payment_amount: orderData?.grandTotal,
          plan_id: slug[0],
        })
      );
    } catch (error) {
      setConfirmAgreement(true);
      setIsScreenLoading(false);
      setFailedReason("Payment failed");
      setScreen(false);
      console.log("error =====", error);
    } finally {
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      await dispatch(onSetFinalDisclosureLoading(false));
      await dispatch(onSetShowDisclosure(false));
    }
  };

  const handleApplePay = async (
    success: boolean,
    paymentIntent?: { id: string },
    failureMessage?: string
  ): Promise<void> => {
    const intentId = paymentIntent?.id;
    if (!success || !intentId || !intentId.startsWith("seti_")) {
      console.error("[Stripe Apple Pay][agreement] invalid confirmation", {
        hasIntent: Boolean(intentId),
        intentType: intentId?.split("_")?.[0] || "unknown",
        success,
      });
      setFailedReason(failureMessage || "Payment failed");
      setScreen(false);
      setConfirmAgreement(true);
      setIsScreenLoading(false);
      dispatch(setIsPaymentProcessing(false));
      return;
    }

    const verificationKey = `AP:${intentId}`;
    if (applePayAgreementInFlightRef.current.has(verificationKey)) {
      console.info(
        "[Stripe Apple Pay][agreement] duplicate submission skipped"
      );
      return;
    }
    applePayAgreementInFlightRef.current.add(verificationKey);
    let agreementCompleted = false;
    dispatch(setIsPaymentProcessing(true));
    setIsScreenLoading(true);
    try {
      const payload = {
        plan_id: orderData?.id,
        lead_id: orderData?.lead_id,
        x_method: "AP",
        transaction_id: intentId,
        x_email: userData?.data?.accountData?.email || "",
        creditcard: "",
        choldername: "",
        aholdername: "",
        desc: "Online Payment",
        description: "Online Payment",
        payment_note: "Payment Agreement",
        cvv: "***",
        expiry: "",
        card_type: "",
        card_last4: "",
        routing_number: "",
        account_number: "",
        bank_name: "",
        bank_address: "",
        search_address: "",
        sign_base64: savedSignUrl,
        clusterid: orderData?.clusterid,
        timezone: getBrowserTimezoneOffset(),
        customer_timezone: getBrowserTimezoneName(),
        slug: actionId || "",
        custom_schedule: 'Y'
      };
      console.info("[Stripe Apple Pay][agreement] submission requested", {
        intentType: intentId.split("_")[0],
        paymentMethod: payload.x_method,
      });
      const res: any = await dispatch(signAgreement(payload)).unwrap();
      if (res?.body?.success) {
        agreementCompleted = true;
        setAmount(res?.body?.data?.total_amount);
        setEmails(res?.body?.data?.email);
        setInvoiceId(res?.body?.data?.invoice_id);
        setScheduleId(res?.body?.data?.schedule_id);
        setScreen(true);
        setConfirmAgreement(true);
        await sendEmail({
          invoice_id: res?.body?.data?.invoice_id,
          emails: res?.body?.data?.email,
        });
        handleStatusOnAccept();

        try {
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_agreement",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "accepted",
              activity_type: "payment_activity",
              clusterid: orderData?.clusterid,
              communication_status: "",
              accounts_id: orderData?.accounts_id,
              payment_amount: orderData?.grandTotal,
              plan_id: orderData?.id,
            })
          );
        } catch (notificationError) {
          console.error(
            "[Stripe Apple Pay][agreement] notification failed",
            notificationError
          );
        }
      } else {
        setFailedReason(
          (typeof res?.body?.data === "string"
            ? res.body.data
            : res?.body?.data?.msg) || "Payment failed"
        );
        setScreen(false);
        setConfirmAgreement(true);
      }
    } catch (error) {
      setConfirmAgreement(true);
      setFailedReason("Payment failed");
      setScreen(false);
      console.error("[Stripe Apple Pay][agreement] submission failed", error);
    } finally {
      if (!agreementCompleted) {
        applePayAgreementInFlightRef.current.delete(verificationKey);
      }
      setIsScreenLoading(false);
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      dispatch(setIsPaymentProcessing(false));
    }
  };

  const checkPastAmount = async (
    method?: string
  ): Promise<boolean | undefined> => {
    try {
      let payload: DuplicatePaymentPayload = {
        accounts_id: userData?.data?.accountData?.id || 0,
        payment_method: method ? method : cardToShortname(selectedMethod),
        amount: parseFloat(orderData.amount?.toString() || "0"),
      };
      let res = await dispatch(duplicatePaymentGet(payload)).unwrap();
      const responseData = res as any;
      const { isDuplicateOrders, duplicateOrdersCount } =
        responseData?.body?.data || {};
      if (isDuplicateOrders && duplicateOrdersCount > 0) {
        return false;
      } else {
        return true;
      }
    } catch (error) {
      console.log("error =====", error);
    }
  };

  const handleCheckDisclosureAndDuplicate = async ({
    method,
  }: {
    method?: string;
  }): Promise<boolean | undefined> => {
    try {
      let moveAhead = !checkedDuplicatePayment
        ? await checkPastAmount(method)
        : true;
      if (!moveAhead) {
        dispatch(onSetFinalDisclosureLoading(false));
        return false;
      } else {
        if (
          (portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
            portalSettings?.payment_agreement_opt === SettingsValue.DEFAULT) &&
          !showFinalDisclosure
        ) {
          dispatch(onSetFinalDisclosureLoading(false));
          dispatch(resetDuplicateOrder());
          requestButtonScroll("disclosure");
          dispatch(onSetShowDisclosure(true));

          return false;
        } else {
          dispatch(resetDuplicateOrder());
          dispatch(resetCheckDuplicatePayment());
          return true;
        }
      }
    } catch (error) {
      console.log("error =====", error);
    }
  };

  const createPayPalIntent = async (): Promise<any> => {
    dispatch(setIsPaymentProcessing(true));
    try {
      const payload = {
        slug: actionId || "",
        is_recurring: "Y",
        phone_number: userData?.data?.accountData?.phone,
        first_name: userData?.data?.accountData?.first_name,
        last_name: userData?.data?.accountData?.last_name,
        accountid: userData?.data?.accountData?.accountid,
        amount: parseFloat(orderData?.amount?.toString() || "0"),
        agent: userData?.data?.accountData?.lead_owner,
        lead_owner: userData?.data?.accountData?.lead_owner,
        lead_id:
          accountLeadData && accountLeadData?.lead_id
            ? accountLeadData?.lead_id
            : 0,
        clusterid: decode(actionId || "")?.split("_")[1],
        success_url: `${window.location.href}?method=paypal&amount=${parseFloat(
          orderData?.amount?.toString() || "0"
        )}&status=success&isRecurring=${true}`,
        cancel_url: `${window.location.href}?method=paypal&amount=${parseFloat(
          orderData?.amount?.toString() || "0"
        )}&status=cancel&isRecurring=${true}`,
        start_date: new Date(),
      };
      let res = await dispatch(createPaypalToken(payload));
      const response: any = res;
      if (response?.payload?.body?.success) {
        const paypalOrderData: PayPalOrderData = {
          url: response?.payload?.body?.result?.url,
          orderId: response?.payload?.body?.result?.invoice_id,
          token: response?.payload?.body?.result?.token,
          sign_base64: savedSignUrl,
          slug: actionId,
          amount: orderData?.clients_schedule_custom_data?.[0]?.amount,
        };
        await dispatch(storePayPalOrder(paypalOrderData));

        let moveAhead = await handleCheckDisclosureAndDuplicate({
          method: "PP",
        });
        if (!moveAhead) {
          return res;
        }
        setTimeout(() => {
          dispatch(setIsPaymentProcessing(false));
          window.location.href = response?.payload?.body?.result?.url;
        }, 0);
      } else {
        setFailedReason(response?.payload?.body?.data?.msg || "Payment failed");
      }
    } catch (error) {
      console.log("createPayPalIntent error =====", error);
    } finally {
      dispatch(setIsPaymentProcessing(false));
    }
  };

  const handlePayPalPayment = async ({
    tokendata,
  }: {
    tokendata: { token: string };
  }): Promise<void> => {
    dispatch(setIsPaymentProcessing(true));
    try {
      let resp = await dispatch(createPaypalPaymentToken(tokendata)).unwrap();
      const responseData: any = resp;
      let slug = decode(actionId || "")?.split("_");
      const payload = {
        plan_id: slug?.[0],
        lead_id: orderData?.lead_id,
        x_method: "PP",
        bcity: userData?.data?.accountData?.city,
        baddress1: userData?.data?.accountData?.address1,
        baddress2: userData?.data?.accountData?.address2,
        bzipcode: userData?.data?.accountData?.postal_code,
        bstate: userData?.data?.accountData?.state,
        bcountry: userData?.data?.accountData?.country,
        x_email: userData?.data?.accountData?.email || "",
        creditcard: "",
        choldername: "",
        desc: "Online Payment",
        description: "Online Payment",
        payment_note: "Payment Agreement",
        aholdername: "",
        cvv: "",
        expiry: "",
        card_type: "",
        card_last4: "",
        routing_number: "",
        account_number: "",
        bank_name: "",
        bank_address: false,
        search_address: "",
        sign_base64: signatureForAgreement(payPalOrder, actionId),
        use_old_card: "1",
        clusterid: slug?.[1],
        cc_token: responseData?.body?.data?.cc_token,
        cc_token2: responseData?.body?.data?.cc_token2,
        cppid: responseData?.body?.data?.cppid,
        timezone: getBrowserTimezoneOffset(),
        customer_timezone: getBrowserTimezoneName(),
        slug: actionId || "",
        custom_schedule: 'Y'
      };
      const res: any = await dispatch(signAgreement(payload)).unwrap();
      if (res?.body?.success) {
        setAmount(res?.body?.data?.total_amount);
        setEmails(res?.body?.data?.email);
        setInvoiceId(res?.body?.data?.invoice_id);
        setScheduleId(res?.body?.data?.schedule_id);
        await sendEmail({
          invoice_id: res?.body?.data?.invoice_id,
          emails: res?.body?.data?.email,
        });
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_agreement",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "accepted",
            activity_type: "payment_activity",
            clusterid: decode(actionId || "")?.split("_")[1],
            communication_status: "",
            accounts_id: orderData?.accounts_id,
            payment_amount: orderData?.grandTotal,
            plan_id: decode(actionId || "")?.split("_")[0],
          })
        );
        setScreen(true);
        handleStatusOnAccept();
      } else {
        setFailedReason(res?.body?.data?.msg);
        setScreen(false);
      }
      setConfirmAgreement(true);
      setIsScreenLoading(false);
    } catch (error) {
      setConfirmAgreement(true);
      setIsScreenLoading(false);
      setFailedReason("Payment failed");
      setScreen(false);
      console.log("error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
    }
  };

  useEffect(() => {
    if (confirmAgreement) {
      dispatch(setIsPaymentProcessing(false));
      runPendingButtonScroll();
    }
  }, [confirmAgreement, dispatch, runPendingButtonScroll]);

  useEffect(() => {
    if (showFinalDisclosure || currentStep > 1) {
      runPendingButtonScroll();
    }
  }, [currentStep, showFinalDisclosure, runPendingButtonScroll]);

  useEffect(() => {
    if (
      searchParams.has("token") &&
      searchParams.has("status") &&
      !searchParams.has("payment_intent") &&
      !searchParams.has("setup_intent")
    ) {
      setIsScreenLoading(true);
      setInitialDisclosureAccept(true);
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      if (searchParams.get("status") === "cancel") {
        setScreen(false);
        setConfirmAgreement(true);
        setIsScreenLoading(false);
        setTimeout(() => {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
          navigate({ search: "" });
        }, 500);
      } else {
        if (!searchParams.has("checked")) {
          searchParams.set("checked", "true");
          handleApprove();
        }
        setTimeout(() => {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
          navigate({ search: "" });
        }, 500);
      }
    } else if (
      (searchParams.has("approval_session_id") ||
        searchParams.has("approval_token_id")) &&
      !searchParams.has("checked") &&
      searchParams.has("status") &&
      searchParams.has("amount") &&
      searchParams.get("method") === "paypal"
    ) {
      setIsScreenLoading(true);
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      searchParams.set("checked", "true");
      const tokendata: { token: string } = {
        token: payPalOrder?.token || "",
      };
      handlePayPalPayment({ tokendata });
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    } else if (
      (searchParams.has("payment_intent") ||
        searchParams.has("setup_intent")) &&
      !searchParams.has("checked") &&
      searchParams.has("isRecurring") &&
      searchParams.has("method") &&
      (searchParams.get("method") === "bank" ||
        searchParams.get("method") === "card" ||
        searchParams.get("method") === "amazonPay" ||
        searchParams.get("method") === "cashApp")
    ) {
      setIsScreenLoading(true);
      setInitialDisclosureAccept(true);
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      searchParams.set("checked", "true");
      handleBankPayment({
        method:
          searchParams.get("method") === "bank"
            ? "ACH"
            : searchParams.get("method") === "card"
              ? "CC"
              : searchParams.get("method") === "cashApp"
                ? "CAPP"
                : "AMZP",
        transaction_id:
          searchParams.get("isRecurring") === "true"
            ? searchParams.get("setup_intent")
            : searchParams.get("payment_intent"),
      });
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    } else if (
      !searchParams.has("token") &&
      !searchParams.has("payment_intent") &&
      !searchParams.has("setup_intent")
    ) {
      setIsScreenLoading(false);
    }
  }, []);

  const handleSubmitGooglePay = async (data: GooglePayData): Promise<void> => {
    dispatch(setIsPaymentProcessing(true));
    setIsScreenLoading(true);
    try {
      if (!data || typeof data !== "object") {
        throw new Error("Google Pay did not return a payment method.");
      }
      const isLegacyGooglePay = "tokenizationData" in data;
      if (!isLegacyGooglePay && !String(data.id).startsWith("seti_")) {
        throw new Error("Invalid Google Pay SetupIntent.");
      }

      console.info("[Stripe Google Pay][agreement] payment submitted", {
        intentType: isLegacyGooglePay ? "legacy" : data.id.split("_")[0],
        paymentMethod: "GP",
      });
      let res = await handleGooglePay("GP", data, savedSignUrl || undefined);
      if (res?.body?.success) {
        setAmount(res?.body?.data?.total_amount);
        setEmails(res?.body?.data?.email);
        setInvoiceId(res?.body?.data?.invoice_id);
        setScheduleId(res?.body?.data?.schedule_id);

        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_agreement",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "accepted",
            activity_type: "payment_activity",
            clusterid: orderData?.clusterid,
            communication_status: "",
            accounts_id: orderData?.accounts_id,
            payment_amount: orderData?.grandTotal,
            plan_id: orderData?.id,
          })
        );
        setScreen(true);
        handleStatusOnAccept();
      } else {
        setFailedReason(
          (typeof res?.body?.data === "string"
            ? res.body.data
            : res?.body?.data?.msg) || "Payment failed"
        );
        setScreen(false);
      }
      setConfirmAgreement(true);
      setIsScreenLoading(false);
      await dispatch(
        addNotification({
          initiated_by: "contact",
          event_type: "payment_agreement",
          userid: userData?.data?.accountData?.leadData?.user_id,
          email: "",
          event_status: "accepted",
          activity_type: "payment_activity",
          clusterid: orderData?.clusterid,
          communication_status: "",
          accounts_id: orderData?.accounts_id,
          payment_amount: orderData?.grandTotal,
          plan_id: orderData?.id,
        })
      );
    } catch (error) {
      setConfirmAgreement(true);
      setIsScreenLoading(false);
      setFailedReason("Payment failed");
      setScreen(false);
      console.log("error =====", error);
    } finally {
      dispatch(setIsPaymentProcessing(false));
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
    }
  };

  const handleOnSubmit = async (): Promise<void> => {
    try {
      if (storePayload) {
        return await handleConfirm(storePayload, false);
      }
    } catch (error) {
      console.log("error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
    }
  };

  const handleAccept = async (): Promise<void> => {
    dispatch(setIsPaymentProcessing(true));
    setIsConfirmLoading(true);
    if (
      (portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
        portalSettings?.payment_agreement_opt === SettingsValue.DEFAULT) &&
      !showFinalDisclosure
    ) {
      dispatch(resetDuplicateOrder());
      dispatch(onSetFinalDisclosureLoading(false));
      setIsConfirmLoading(false);
      requestButtonScroll("disclosure");
      dispatch(onSetShowDisclosure(true));
      return;
    }
    setIsConfirmLoading(false);
    await dispatch(onSetFinalDisclosureLoading(true));
    try {
      if (storePayload?.isAddNewCard) {
        await handleOnSubmit();
      } else {
        switch (selectedMethod) {
          case "Card":
            if (
              paymentPermission?.cc_gateway_name?.name ===
                PaymentGateway.STRIPE &&
              cardRef.current
            ) {
              await cardRef.current.callHandleClick();
            } else {
              await handleOnSubmit();
            }
            break;
          case "Amazon Pay":
            if (amazonRef.current) {
              await amazonRef.current.callHandleClick();
            } else {
              await handleOnSubmit();
            }
            break;
          case "Bank":
            let check = checkBankManualFormOrPlaid({
              manual_ach: portalSettings?.manual_ach,
              validate_bank_account: portalSettings?.validate_bank_account,
              isManualForm,
            });
            if (
              (paymentPermission?.ach_gateway_name?.name ===
                PaymentGateway.STRIPE ||
                check) &&
              bankRef.current
            ) {
              await bankRef.current.callHandleClick();
            } else {
              await handleOnSubmit();
            }
            break;
          case "Cash App Pay":
            if (cashAppRef.current) {
              await cashAppRef.current.callHandleClick();
            }
            break;
          case "Wire":
            await handleOnSubmit();
            break;
          case "PayPal":
            if (
              (portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
                portalSettings?.payment_agreement_opt ===
                  SettingsValue.DEFAULT) &&
              !showFinalDisclosure
            ) {
              dispatch(resetDuplicateOrder());
              dispatch(onSetFinalDisclosureLoading(false));
              dispatch(onSetShowDisclosure(true));
            } else {
              window.location.href = payPalOrder.url;
            }
            break;
          case "Google Pay":
            if (tokenData?.token) {
              await handleSubmitGooglePay(tokenData.token as GooglePayData);
            }
            break;
          case "Apple Pay":
            if (tokenData?.token) {
              await handleApplePay(true, tokenData.token as { id: string });
            }
            break;
          default:
            console.log("selectedMethod =====", selectedMethod);
        }
      }
    } catch (error) {
      console.log("handleAccept error =====", error);
    } finally {
      requestButtonScroll("disclosure");
      dispatch(setIsPaymentProcessing(false));
    }
  };

  const handlePlaidBankPayment = async (
    values: {
      choldername?: string;
      x_payment_method?: string;
      [key: string]: any;
    },
    isCCTokenAvailable: boolean
  ): Promise<void> => {
    try {
      let payload: CardData = {
        ...values,
        choldername: userData?.data?.accountData?.first_name,
        x_payment_method: "ACH",
        isAddNewCard: false,
      };
      return await handleConfirm(payload, isCCTokenAvailable);
    } catch (error) {
      console.log("handlePlaidBankPayment error =====", error);
    }
  };

  const verifyStripeCardPayment = async ({
    intent,
    email,
    error,
  }: VerifyStripeCardPaymentParams): Promise<void> => {
    let slug = decode(actionId || "")?.split("_");
    console.log(" ======", intent, email, error);

    if (error) {
      setFailedReason(error.message);
      setScreen(false);
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      setConfirmAgreement(true);
    } else {
      dispatch(setIsPaymentProcessing(true));
      try {
        let payload = {
          plan_id: slug?.[0],
          lead_id: orderData?.lead_id,
          x_method: "CC",
          transaction_id: intent,
          x_email: userData?.data?.accountData?.email || "",
          creditcard: "",
          choldername: "",
          aholdername: "",
          desc: "Online Payment",
          description: "Online Payment",
          payment_note: "Payment Agreement",
          cvv: "***",
          expiry: "",
          card_type: "",
          card_last4: "",
          routing_number: "",
          account_number: "",
          bank_name: "",
          bank_address: "",
          search_address: "",
          sign_base64: agreementSignBase64,
          is3ds: true,
          clusterid: slug?.[1],
          email,
          timezone: getBrowserTimezoneOffset(),
          customer_timezone: getBrowserTimezoneName(),
          slug: actionId || "",
          custom_schedule: 'Y'
        };

        const res: any = await dispatch(signAgreement(payload)).unwrap();

        if (res?.body?.success) {
          setAmount(res?.body?.data?.total_amount);
          setEmails(email || res?.body?.data?.email);
          setInvoiceId(res?.body?.data?.invoice_id);
          setScheduleId(res?.body?.data?.schedule_id);
          setScreen(true);
          await sendEmail({
            invoice_id: res?.body?.data?.invoice_id,
            emails: email || res?.body?.data?.email,
          });

          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_agreement",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "accepted",
              activity_type: "payment_activity",
              clusterid: slug[1],
              communication_status: "",
              accounts_id: orderData?.accounts_id,
              payment_amount: orderData?.grandTotal,
              plan_id: slug[0],
            })
          );
          handleStatusOnAccept();
        } else {
          setFailedReason(res.body.data.msg);
          setScreen(false);
        }
        setConfirmAgreement(true);
      } catch (error: any) {
        console.log("error ======", error);
        setFailedReason(error.message);
        setScreen(false);
      } finally {
        dispatch(resetDuplicateOrder());
        dispatch(resetCheckDuplicatePayment());
        dispatch(onSetFinalDisclosureLoading(false));
        dispatch(onSetShowDisclosure(false));
      }
    }
  };

  const verifyStaxCardBank = async ({
    cc_token,
    cc_token2,
    x_method,
  }: VerifyStaxCardPaymentParams): Promise<void> => {
    let slug = decode(actionId || "")?.split("_");
    dispatch(setIsPaymentProcessing(true));
    try {
      //  Step 1 : create wallets profile with stax and get wallet id
      let payload = {
        cc_token,
        cc_token2,
        x_method,
        email: userData?.data?.accountData?.email || "",
        accountid: userData?.data?.accountData?.accountid,
        clusterid: slug?.[1],
        lead_id: accountLeadData?.lead_id || orderData?.lead_id,
      };
      let res: any = await dispatch(staxProfile(payload)).unwrap();
      if (res?.body?.success) {
        let selectedData = res?.body?.data?.paymentMethod;

        // step 2 : make payment using the wallet id to make a payment
        let paymentPayload = {
          plan_id: orderData?.id,
          lead_id: orderData?.lead_id,
          x_method: x_method || "",
          bcity: selectedData?.billing_city,
          baddress1: selectedData?.billing_address,
          baddress2: selectedData?.billing_address2,
          bzipcode: selectedData?.billing_zip,
          bstate: selectedData?.billing_state,
          bcountry: selectedData?.billing_country,
          x_email: selectedData?.email || "",
          creditcard: selectedData?.cc_4digit,
          choldername: selectedData?.account_name,
          desc: "Online Payment",
          description: "Online Payment",
          payment_note: "Payment Agreement",
          aholdername: selectedData?.account_name,
          cvv: selectedData?.cc_cvv,
          expiry: selectedData?.cc_expiry,
          card_type: selectedData?.cc_type,
          card_last4: selectedData?.cc_4digit,
          routing_number: selectedData?.routing_number,
          account_number: selectedData?.account_number,
          bank_name: selectedData?.bank_name,
          bank_address: selectedData?.bank_address,
          search_address: "",
          sign_base64: agreementSignBase64,
          cc_token: selectedData?.cc_token,
          cc_token2: selectedData?.cc_token2,
          use_old_card: "1",
          cppid: selectedData?.id,
          clusterid: orderData?.clusterid,
          custom_schedule: 'Y'
        };

        const resp: any = await dispatch(
          signAgreement(paymentPayload)
        ).unwrap();

        if (resp?.body?.success) {
          setAmount(resp?.body?.data?.total_amount);
          setEmails(resp?.body?.data?.email);
          setInvoiceId(resp?.body?.data?.invoice_id);
          setScheduleId(resp?.body?.data?.schedule_id);
          setScreen(true);
          await sendEmail({
            invoice_id: resp?.body?.data?.invoice_id,
            emails: resp?.body?.data?.email,
          });

          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_agreement",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "accepted",
              activity_type: "payment_activity",
              clusterid: slug[1],
              communication_status: "",
              accounts_id: orderData?.accounts_id,
              payment_amount: orderData?.grandTotal,
              plan_id: slug[0],
            })
          );
          handleStatusOnAccept();
        } else {
          setFailedReason(resp.body.data.msg || "Payment failed");
          setScreen(false);
        }
        setConfirmAgreement(true);
      }
    } catch (error) {
      setScreen(false);
      setFailedReason("Payment failed");
      console.log("verifyStaxCardBank error =====", error);
    } finally {
      dispatch(setIsPaymentProcessing(false));
    }
  };

  const isStax = useMemo(() => {
    if (
      selectedMethod === "CC" &&
      paymentPermission?.cc_gateway_name?.name === PaymentGateway.STAX
    ) {
      return true;
    } else if (
      selectedMethod === "ACH" &&
      paymentPermission?.ach_gateway_name?.name === PaymentGateway.STAX
    ) {
      return true;
    }

    return false;
  }, [selectedMethod, paymentPermission]);

  return (
    <div className="h-full" {...testAttrs("flow", "Action payment flow")}>
      <div
        ref={paymentFlowTopRef}
        className="h-0"
        data-testid="action-payment-flow-top"
      />
      {isScreenLoading ? (
        <div
          className="w-full h-full flex justify-center items-center"
          {...testAttrs("loading", "Loading action payment")}
        >
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
        </div>
      ) : (
        <>
          <div
            ref={stepperAnchorRef}
            className="h-0 lg:hidden block"
            style={{ scrollMarginTop: "84px" }}
            data-testid="action-payment-stepper-anchor"
          />

          <div
            className={showFinalDisclosure ? "hidden none" : "w-full h-full"}
            {...testAttrs("steps", "Action payment steps")}
          >
            {/* desktop and laptop view */}
            <div
              className={`lg:flex hidden h-full relative w-full flex-grow justify-center overflow-y-auto ${
                confirmAgreement ? "" : "pt-[100px]"
              }`}
              {...testAttrs("desktop-steps", "Action payment desktop steps")}
            >
              {!confirmAgreement ? (
                <div className="grid min-h-full w-full max-w-[760px] grid-cols-[41px_minmax(0,1fr)] content-start gap-x-7 gap-y-[33px] xl:px-10 px-6 pb-8">
                  <div
                    className="contents"
                    {...testAttrs("desktop-stepper", "Agreement payment steps")}
                  >
                    <DesktopStepRailItem
                      step={1}
                      currentStep={currentStep}
                      completedSteps={completedSteps}
                      nextCircleOffset={STEP_RAIL_DIVIDER_OFFSET}
                      onClick={handleOnClick}
                    />
                  </div>
                  <div className="min-w-0 pr-2">
                    <AgreementDetails
                      currentStep={currentStep}
                      tempdata={tempdata}
                      handleNext={handleNext}
                      isFee={isFee}
                    />
                  </div>
                  <DesktopStepRailItem
                    step={2}
                    currentStep={currentStep}
                    completedSteps={completedSteps}
                    circleOffset={STEP_RAIL_DIVIDER_OFFSET}
                    nextCircleOffset={STEP_RAIL_DIVIDER_OFFSET}
                    onClick={handleOnClick}
                  />
                  <div className="min-w-0 pr-2">
                    <SignAgreement
                      currentStep={currentStep}
                      tempData={tempdata}
                      cardData={cardData}
                      orderData={orderData}
                      handleBack={handleBack}
                      handleSaveSign={storeSign}
                      savedSignature={savedSignUrl}
                      savedConsentAt={savedConsentAt}
                      handleConsent={storeConsent}
                      isStax={isStax}
                    />
                  </div>
                  <DesktopStepRailItem
                    step={3}
                    currentStep={currentStep}
                    completedSteps={completedSteps}
                    circleOffset={STEP_RAIL_DIVIDER_OFFSET}
                    isLastStep
                    onClick={handleOnClick}
                  />
                  <div
                    className="min-w-0 pr-2"
                    data-testid="action-payment-desktop-step-content"
                  >
                    <PaymentMethods
                      currentStep={currentStep}
                      handleNext={handleConfirm}
                      handleBack={handleBack}
                      custom_conv={orderData?.custom_conv}
                      tempData={tempdata}
                      setCardData={setCardData}
                      paymentMethods={orderData?.payment_method}
                      setCurrentStep={setCurrentStep}
                      amount={(
                        orderData?.clients_schedule_custom_data?.[0]?.amount ||
                        0
                      ).toString()}
                      useStripeGooglePay={isDesktopViewport}
                      useStripeApplePay={isDesktopViewport}
                      handleGooglePay={(data: any, amount: any) => {
                        handleSubmitGooglePay(data);
                      }}
                      createPayPalIntent={createPayPalIntent}
                      orderData={orderData}
                      isGoogleLoading={isGooglePayLoading}
                      selectedMethod={selectedMethod}
                      setSelectedMethod={setSelectedMethod}
                      handleApplePay={handleApplePay}
                      isLoading={isConfirmLoading}
                      setIsLoading={setIsConfirmLoading}
                      isRecurring={
                        !moment(scheduled?.[0]?.billing_date).isSame(
                          moment(),
                          "days"
                        )
                      }
                      cashAppRef={cashAppRef}
                      bankRef={bankRef}
                      cardRef={cardRef}
                      amazonRef={amazonRef}
                      setStorePayload={setStorePayload}
                      tokenData={tokenData}
                      setTokenData={setTokenData}
                      handlePlaidBankPayment={handlePlaidBankPayment}
                      isManualForm={isManualForm}
                      setIsManualForm={setIsManualForm}
                      action={action}
                      verifyStripeCardPayment={verifyStripeCardPayment}
                      verifyStaxPayment={verifyStaxCardBank}
                      onFinalDisclosureOpen={() =>
                        requestButtonScroll("disclosure")
                      }
                    />
                  </div>
                </div>
              ) : (
                confirmAgreement &&
                (screen ? (
                  <div
                    className="min-h-full w-full grid place-items-center py-8"
                    {...testAttrs("success-state", "Payment agreement success")}
                  >
                    <PaymentAgreementProcessSucces
                      onDownloadPdf={onDownloadPdf}
                      defaultEmails={emails}
                      onSendEmail={onSendEmail}
                      amount={
                        amount
                          ? amount
                          : (orderData?.amount || 0) +
                            (orderData?.cf_amount || 0)
                      }
                      invoiceId={invoiceId}
                      tempData={tempdata}
                      scheduled={scheduled}
                      orderData={orderData}
                      cardData={cardData}
                      scheduleId={scheduleId}
                    />
                  </div>
                ) : (
                  <div
                    className="min-h-full w-full grid place-items-center py-8"
                    {...testAttrs("failed-state", "Payment agreement failed")}
                  >
                    <PaymentAgreementFailed
                      failedReason={failedReason}
                      handleUpdatePaymentMethod={handleUpdatePaymentMethod}
                    />
                  </div>
                ))
              )}
            </div>

            {/* tablet and mobile view */}
            <div
              className="lg:hidden flex h-full flex-col w-full pt-5 px-5"
              {...testAttrs("mobile-steps", "Action payment mobile steps")}
            >
              {!confirmAgreement && (
                <div
                  {...testAttrs("mobile-stepper", "Agreement payment steps")}
                >
                  <Stepper
                    currentStep={currentStep}
                    Steps={3}
                    completedSteps={completedSteps}
                    handleOnClick={handleOnClick}
                  />
                </div>
              )}
              {!confirmAgreement && currentStep === 1 && (
                <div
                  className="py-4 h-full"
                  data-testid="action-payment-mobile-agreement-details"
                >
                  <AgreementDetails
                    currentStep={currentStep}
                    tempdata={tempdata}
                    handleNext={handleNext}
                    isFee={isFee}
                  />
                </div>
              )}
              {!confirmAgreement && currentStep === 2 && (
                <div
                  className="py-4 h-full"
                  data-testid="action-payment-mobile-sign-agreement"
                >
                  <SignAgreement
                    currentStep={currentStep}
                    tempData={tempdata}
                    cardData={cardData}
                    orderData={orderData}
                    handleBack={handleBack}
                    handleSaveSign={storeSign}
                    savedSignature={savedSignUrl}
                    savedConsentAt={savedConsentAt}
                    handleConsent={storeConsent}
                    isStax={isStax}
                  />
                </div>
              )}

              {!confirmAgreement && currentStep === 3 && (
                <div
                  className="pt-4 h-full"
                  data-testid="action-payment-mobile-payment-method"
                >
                  <PaymentMethods
                    currentStep={currentStep}
                    handleNext={handleConfirm}
                    handleBack={handleBack}
                    custom_conv={orderData?.custom_conv}
                    tempData={tempdata}
                    setCardData={setCardData}
                    paymentMethods={orderData?.payment_method}
                    setCurrentStep={setCurrentStep}
                    amount={(
                      orderData?.clients_schedule_custom_data?.[0]?.amount || 0
                    ).toString()}
                    useStripeGooglePay={!isDesktopViewport}
                    useStripeApplePay={!isDesktopViewport}
                    handleGooglePay={(data: any, amount: any) => {
                      handleSubmitGooglePay(data);
                    }}
                    createPayPalIntent={createPayPalIntent}
                    orderData={orderData}
                    isGoogleLoading={isGooglePayLoading}
                    selectedMethod={selectedMethod}
                    setSelectedMethod={setSelectedMethod}
                    handleApplePay={handleApplePay}
                    isLoading={isConfirmLoading}
                    setIsLoading={setIsConfirmLoading}
                    isRecurring={
                      !moment(scheduled?.[0]?.billing_date).isSame(
                        moment(),
                        "days"
                      )
                    }
                    cashAppRef={cashAppRef}
                    bankRef={bankRef}
                    cardRef={cardRef}
                    amazonRef={amazonRef}
                    setStorePayload={setStorePayload}
                    tokenData={tokenData}
                    setTokenData={setTokenData}
                    handlePlaidBankPayment={handlePlaidBankPayment}
                    isManualForm={isManualForm}
                    setIsManualForm={setIsManualForm}
                    action={action}
                    verifyStripeCardPayment={verifyStripeCardPayment}
                    verifyStaxPayment={verifyStaxCardBank}
                    onFinalDisclosureOpen={() =>
                      requestButtonScroll("disclosure")
                    }
                  />
                </div>
              )}

              {confirmAgreement &&
                (screen ? (
                  <div
                    className="w-full flex justify-center items-start"
                    {...testAttrs(
                      "mobile-success-state",
                      "Payment agreement success"
                    )}
                  >
                    <PaymentAgreementProcessSucces
                      onDownloadPdf={onDownloadPdf}
                      defaultEmails={emails}
                      onSendEmail={onSendEmail}
                      amount={
                        amount
                          ? amount
                          : (orderData?.amount || 0) +
                            (orderData?.cf_amount || 0)
                      }
                      invoiceId={invoiceId}
                      tempData={tempdata}
                      scheduled={scheduled}
                      orderData={orderData}
                      cardData={cardData}
                      scheduleId={scheduleId}
                    />
                  </div>
                ) : (
                  <div
                    className=" w-full h-full grid place-items-center "
                    {...testAttrs(
                      "mobile-failed-state",
                      "Payment agreement failed"
                    )}
                  >
                    <PaymentAgreementFailed
                      failedReason={failedReason}
                      handleUpdatePaymentMethod={handleUpdatePaymentMethod}
                    />
                  </div>
                ))}
            </div>
          </div>

          <div
            ref={disclosureRef}
            className={`h-full lg:max-h-[var(--shell-vh)] relative content w-full lg:pt-20 lg:pb-[40px] md:p-10 p-5 max-w-[740px] mx-auto ${
              !showFinalDisclosure ? `hidden none` : ""
            }`}
            style={{ scrollMarginTop: "84px" }}
            {...testAttrs("final-disclosure", "Final payment disclosure")}
          >
            <DisclosureFinal
              generalMarkers={generalMarkers}
              handleAccept={handleAccept}
            />
          </div>
        </>
      )}
      <CustomModal
        signModal={false}
        isLoading={false}
        isOpen={isDuplicateOrders && duplicateOrdersCount > 0}
        title="Alert"
        doneText="Complete Payment"
        className="max-w-[600px] w-full h-fit rounded-[10px]"
        cancleText="Cancel"
        onDoneClick={() => {
          dispatch(resetDuplicateOrder());
          handleAccept();
        }}
        onCloseClick={() => {
          dispatch(resetCheckDuplicatePayment());
          dispatch(resetDuplicateOrder());
        }}
        children={
          <React.Fragment>
            <div
              className="pt-5 lg:text-xl text-txtPrimary lg:leading-[30px] leading-[24px]"
              {...testAttrs("duplicate-payment-alert")}
            >
              A similar payment was made in the last 10 minutes. Click 'Complete
              Payment' if this is not a duplicate.
            </div>
          </React.Fragment>
        }
      />
    </div>
  );
};

const ActionPaymentMethod = (props: ActionPaymentMethodProps) => (
  <TestIdScope scope="action-payment">
    <ActionPaymentMethodContent {...props} />
  </TestIdScope>
);

export default ActionPaymentMethod;
